import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "../lib/api";

/**
 * The keys that work in one space.
 *
 * A key names a space and can reach nothing else (ADR 000041), so it is minted
 * from inside the space it is for rather than from account settings: a
 * credential scoped to one space, created from a screen about all of them,
 * invites exactly the confusion the scoping removes.
 *
 * The secret exists once, in the response that creates it, and is never
 * recoverable. That is the shape of this screen: show it prominently, once, with
 * a configuration block that is complete because the space is in the key.
 *
 * Minting one is the moment somebody decides how much of their work a machine
 * may touch, so the blurb says what it reaches and what bounds it. ADR 000058
 * removed the rule that kept a key out of the timeline and the treatment, and
 * moved the review it stood in for out of the route table and into the product;
 * a reader who is not told that will not scope a key narrowly and will not know
 * that revoking it is the thing to reach for when a loop goes wrong.
 */
export function SpaceKeysRoute() {
  const { spaceId = "" } = useParams();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<{ id: string; secret: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  const keys = useQuery({ queryKey: ["agent-keys"], queryFn: () => api.listAgentKeys() });

  const create = useMutation({
    mutationFn: () => api.createAgentKey(spaceId as never, { name: name.trim() }),
    onSuccess: async (created) => {
      setName("");
      setCopied(false);
      setFailure(null);
      // Held in memory only. Nothing stores it, and a reload loses it, which is
      // the same promise the server makes.
      setFresh({ id: created.key.id, secret: created.secret });
      await queryClient.invalidateQueries({ queryKey: ["agent-keys"] });
    },
    onError: (error) => setFailure(describe(error)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeAgentKey(id as never),
    onSuccess: async (_, id) => {
      if (fresh?.id === id) setFresh(null);
      await queryClient.invalidateQueries({ queryKey: ["agent-keys"] });
    },
    onError: (error) => setFailure(describe(error)),
  });

  if (space.isPending) return <p role="status">Loading…</p>;
  if (space.error) {
    return (
      <section className="empty">
        <h1>Could not load this space</h1>
        <p>
          <Link to={`/spaces/${spaceId}`}>Back</Link>
        </p>
      </section>
    );
  }

  const here = keys.data?.filter((key) => key.spaceId === spaceId) ?? [];

  return (
    <section>
      <header className="page-head">
        <p className="crumb">
          <Link to={`/spaces/${spaceId}`}>{space.data.name}</Link>
        </p>
        <h1>Agent keys</h1>
        <p className="muted">
          For the MCP server, and anything else that works in scenix without a person sitting in
          front of it. A key made here works in {space.data.name} and nowhere else, so nothing you
          connect it to has to be told which space to use. It carries your own role in this space:
          it can bring content in and describe it, and it can write the treatment, the chain and the
          notes, the same as you can.
        </p>
        <p className="muted">
          Three things bound it, and none of them is somebody approving its work first. Every write
          carries the version it was made against, so it cannot overwrite an edit it never saw. The
          things it can delete come back: an asset, a moment, a document or a project spends thirty
          days in a recovery window first. And revoking a key stops it at once, without touching
          your own access. What outlives revoking it is refused outright: a key cannot invite
          anybody, rename the space, or put back something a person deleted.
        </p>
      </header>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          value={name}
          aria-label="What this key is for"
          placeholder="claude desktop"
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" disabled={!name.trim() || create.isPending}>
          {create.isPending ? "Creating…" : "Create a key"}
        </button>
      </form>

      {fresh && (
        <div className="fresh-key">
          <p>
            <strong>Copy this now.</strong> It is not stored and cannot be shown again. If you lose
            it, revoke the key and make another.
          </p>
          <code>{fresh.secret}</code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(fresh.secret).then(() => setCopied(true));
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>

          <p className="muted">
            Send it as <code>Authorization: Bearer …</code> to this space's MCP address. For
            connecting Claude itself, there is nothing to paste at all: see{" "}
            <Link to={`/spaces/${spaceId}/connect`}>Connect an agent</Link>.
          </p>
        </div>
      )}

      <h2 className="section-head">Keys for this space</h2>

      {keys.isPending ? (
        <p role="status">Loading…</p>
      ) : keys.error ? (
        <p className="error">{describe(keys.error)}</p>
      ) : here.length === 0 ? (
        <p className="muted">No keys for this space yet.</p>
      ) : (
        <ul className="keys">
          {here.map((key) => (
            <li key={key.id} className={key.revokedAt ? "is-revoked" : ""}>
              <span className="key-body">
                <strong>{key.name}</strong>
                <span className="key-meta">
                  {/* Enough to tell two keys apart, and not enough to use. */}
                  sk_scenix_…{key.hint}
                  {" · "}
                  created {shortDate(key.createdAt)}
                  {key.lastUsedAt ? ` · last used ${shortDate(key.lastUsedAt)}` : " · never used"}
                  {key.expiresAt && ` · expires ${shortDate(key.expiresAt)}`}
                  {key.revokedAt && " · revoked"}
                </span>
              </span>
              {!key.revokedAt && (
                <button
                  type="button"
                  className="ghost danger"
                  disabled={revoke.isPending}
                  onClick={() => {
                    // A revoked key cannot be un-revoked, and whatever was
                    // using it stops working immediately.
                    if (window.confirm(`Revoke "${key.name}"? Anything using it stops working.`)) {
                      revoke.mutate(key.id);
                    }
                  }}
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="muted">
        <Link to={`/spaces/${spaceId}/mcp`}>Set up an agent harness</Link> with one of these keys,
        or <Link to="/settings/keys">see every key on your account</Link>, across spaces.
      </p>
    </section>
  );
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
